import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flashlight, FlashlightOff, Images, SwitchCamera, TriangleAlert, Zap, ZapOff } from 'lucide-react';
import { blurScore, isBlurry, regionOf } from '../../lib/scan/blur.js';
import { DETECT_WIDTH } from '../../lib/scan/detect.js';
import type { DetectRequest, DetectResult } from '../../lib/scan/detect.worker.js';
import { quadDrift, scaleQuad, type Point, type Quad } from '../../lib/scan/geometry.js';
import DetectWorker from '../../lib/scan/detect.worker.ts?worker';
import { useContainRect } from './useContainRect.js';
import { releaseCamera } from '../../lib/scan/cameraLifecycle.js';

/**
 * SCAN-004 — the viewfinder.
 *
 * A camera, an outline that sticks to the paper, and a shutter that presses
 * itself. The last of those is the point: the reason photographing a document
 * on a phone produces a bad picture is that pressing the button is what moves
 * the phone, and the frame that gets kept is the one taken at the moment of
 * being nudged. Watching for the outline to stop moving and taking the picture
 * then removes the press from the photograph entirely.
 */

/** How still is still: the worst corner moving less than this share of the
 *  frame, between one look and the next. Two per cent of a phone screen is
 *  about a finger's width of tolerance at arm's length. */
const STEADY_DRIFT = 0.02;

/** How long it has to stay that still before the shutter goes. Short enough
 *  not to feel like a wait, long enough that a hand swinging past a document
 *  on its way somewhere else does not photograph it. */
const STEADY_MS = 700;

/** Silence after a shot, so one page held still is not photographed nine
 *  times while the hand moves towards the next one. */
const COOLDOWN_MS = 1600;

/** How often to look for the page. The detector's own answer is what actually
 *  paces this — a look is never started while one is outstanding — so on a
 *  phone this is a floor rather than a rate, and on a laptop it is the rate. */
const LOOK_EVERY_MS = 45;

/**
 * How far apart the two frames being compared for stillness are, in
 * milliseconds.
 *
 * Not the same thing as the interval between looks, and it stopped being the
 * same thing when the looks got quicker. Stillness is measured as movement
 * between one frame and another, so measuring it against a frame from ten
 * milliseconds ago instead of seventy calls a slow drift still — the shutter
 * then fires while the phone is being lowered towards the desk. Pinning the
 * comparison to a span of time rather than to a count of frames keeps the
 * meaning of "steady" the same on every device.
 */
const DRIFT_SPAN_MS = 70;

/**
 * The resolution to ask the camera for.
 *
 * Above what a scan keeps rather than at the sensor's maximum. A photographed
 * page is put away at 2400 pixels down its long side — 200 dots per inch across
 * an A4 sheet, which is the low end of what text recognition wants and past
 * what anybody reads on a screen — so every pixel a 4K stream carries beyond
 * this is scaled away and thrown out. It was not free on the way: four times
 * the pixels through the camera pipeline, through the preview, and through
 * every frame the detector looks at, on the device least able to afford them.
 */
const STREAM_WIDTH = 2560;
const STREAM_HEIGHT = 1440;

/**
 * Section 3 — how often the picture is checked for blur, where it is checked.
 *
 * Slower than the outline is looked for: a check shrinks the page to 640 pixels
 * rather than 240, and blur that matters lasts longer than a third of a second.
 */
const BLUR_EVERY_MS = 300;

/**
 * How many blurry checks in a row before the viewfinder says so: about a second.
 * The lens hunting for focus as the phone comes down over the paper is blurry
 * for a moment on every page, and a warning that flashes on every page is one
 * nobody reads.
 */
const BLUR_LOOKS = 3;

type Status = 'starting' | 'live' | 'waiting' | 'denied' | 'unavailable';

/**
 * The lamp on the back of the phone.
 *
 * It is not in the standard track types, and it is not on most devices either:
 * Chrome on Android has it, desktop browsers have no lamp to offer, and Safari
 * will not hand one over on any platform. So it is asked for rather than
 * assumed, and the button appears only where the answer was yes — a torch
 * button that does nothing is worse than no torch button, because the room is
 * dark and the person is pressing it.
 */
type LampCapabilities = MediaTrackCapabilities & { torch?: boolean };
type LampConstraint = MediaTrackConstraintSet & { torch?: boolean };

function hasLamp(track: MediaStreamTrack | null): boolean {
  try {
    return (track?.getCapabilities?.() as LampCapabilities | undefined)?.torch === true;
  } catch {
    return false;
  }
}

interface Camera {
  id: string;
  /** As the browser gives it, which before permission is granted is empty. */
  label: string;
}

export function CameraView({
  onCapture,
  onPickFiles,
  disabled,
  checkBlur = false,
  documentMode = false,
}: {
  /** The full-resolution frame, and where the page was found in it. */
  onCapture: (frame: ImageBitmap, quad: Quad | null) => void;
  onPickFiles: () => void;
  /** True while the page count is at its limit, or a shot is being processed. */
  disabled: boolean;
  /**
   * Section 3 — check the picture for blur while it is being lined up: the
   * viewfinder says so when it has been blurry for about a second, and the
   * shutter that presses itself waits for a sharp frame. The button is still
   * the person's to press. Off unless a caller asks, so a scan that never asked
   * is as it was.
   */
  checkBlur?: boolean;
  /** Explicit opt-in to paper detection and automatic shutter. Photo mode keeps the full frame. */
  documentMode?: boolean;
}) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const frameRef = useRef<HTMLCanvasElement | null>(null);
  const [box, setBox] = useState<HTMLDivElement | null>(null);

  const [suspended, setSuspended] = useState(() => document.hidden);
  useEffect(() => {
    const changed = () => setSuspended(document.hidden);
    document.addEventListener('visibilitychange', changed);
    const backgrounded = () => setSuspended(true);
    window.addEventListener('pagehide', backgrounded);
    return () => { document.removeEventListener('visibilitychange', changed); window.removeEventListener('pagehide', backgrounded); };
  }, []);

  const [status, setStatus] = useState<Status>('starting');
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  const [quad, setQuad] = useState<Quad | null>(null);
  const [steady, setSteady] = useState(false);
  const [autoShutter, setAutoShutter] = useState(true);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [using, setUsing] = useState<string | null>(null);
  const [active, setActive] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [lamp, setLamp] = useState(false);
  const [lampable, setLampable] = useState(false);
  /** Section 3 — the picture has been blurry for the last few checks. */
  const [shaky, setShaky] = useState(false);

  /* The track the lamp belongs to. Held apart from the stream because the
     stream is the effect's, and the button is not inside the effect. */
  const trackRef = useRef<MediaStreamTrack | null>(null);

  /* The loop reads all of these, and none of them should restart the camera. */
  const quadRef = useRef<Quad | null>(null);
  const autoRef = useRef(autoShutter);
  const disabledRef = useRef(disabled);
  const captureRef = useRef(onCapture);
  const checkBlurRef = useRef(checkBlur);
  autoRef.current = autoShutter;
  disabledRef.current = disabled;
  captureRef.current = onCapture;
  checkBlurRef.current = checkBlur;

  const picture = useContainRect(box, natural);

  /**
   * The lamp, switched by asking the track for it.
   *
   * There is no separate torch API: the light is a constraint on the camera
   * that is already open, which is why it can only be lit while the viewfinder
   * is live and why it goes out on its own when the camera is released.
   */
  const flip = async (): Promise<void> => {
    const track = trackRef.current;
    if (!track) return;
    const wanted = !lamp;
    try {
      await track.applyConstraints({ advanced: [{ torch: wanted } as LampConstraint] });
      setLamp(wanted);
    } catch {
      // A camera that listed a lamp and then would not light it. The button
      // goes back to how it was rather than lying about the room.
      setLampable(hasLamp(track));
    }
  };

  const shoot = useCallback(async () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2 || disabledRef.current) return;
    // From the video itself rather than the shrunk copy the detector reads:
    // that one is 240 pixels wide and exists only to be measured.
    const frame = await createImageBitmap(video);
    captureRef.current(frame, documentMode ? quadRef.current : null);
  }, [documentMode]);

  /*
   * The camera, the worker and the loop between them, started and stopped
   * together. This component is mounted only while the viewfinder is on screen,
   * so unmounting is what releases the camera — and the indicator light going
   * out when the dialog closes is the only proof of that a person ever gets.
   *
   * Restarted when another camera is chosen, because a stream is of one camera:
   * switching lenses is opening a second stream and dropping the first.
   */
  useEffect(() => {
    if (suspended) { setStatus('waiting'); return; }
    let stopped = false;
    let stream: MediaStream | null = null;
    let raf = 0;
    let worker: Worker | null = null;
    let waiting = false;
    let sequence = 0;
    let lastLook = 0;
    let steadySince = 0;
    let quietUntil = 0;
    let previous: Quad | null = null;
    let previousAt = 0;
    /* Set once a resize the graphics hardware can do has been refused. */
    let resizeUnsupported = false;
    /* Section 3 — the last blur check: blurry, sharp, or not measured (null),
       which counts as sharp. And how many blurry ones in a row. */
    let blurry: boolean | null = null;
    let blurryRun = 0;
    let checking = false;
    let lastCheck = 0;

    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus('unavailable');
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          // A camera the person picked is the camera, and asking for the rear
          // one as well would let a browser decide between the two of them.
          video: using
            ? { deviceId: { exact: using }, width: { ideal: STREAM_WIDTH }, height: { ideal: STREAM_HEIGHT } }
            : {
                facingMode: { ideal: 'environment' },
                width: { ideal: STREAM_WIDTH },
                height: { ideal: STREAM_HEIGHT },
              },
          audio: false,
        });
      } catch (cause) {
        if (stopped) return;
        setStatus((cause as DOMException)?.name === 'NotAllowedError' ? 'denied' : 'unavailable');
        return;
      }
      if (stopped || !videoRef.current) {
        stream?.getTracks().forEach((track) => track.stop());
        return;
      }
      videoRef.current.srcObject = stream;
      let playing = false;
      try {
        await videoRef.current.play();
        playing = !videoRef.current.paused && videoRef.current.videoWidth > 0;
      } catch {
        // Autoplay refused — a black frame is not "live". Keep waiting so the
        // shutter stays disabled until a real picture is on screen.
        playing = false;
      }
      if (!stopped) {
        setStatus(playing ? 'live' : 'waiting');
        const video = videoRef.current;
        const onPlaying = () => {
          if (!stopped && video.videoWidth > 0) setStatus('live');
        };
        video.addEventListener('playing', onPlaying);
        video.addEventListener('loadeddata', onPlaying);
      }

      /*
       * The lamp is the open camera's, so it is looked for on every open and
       * reported off on every open: a lens that was lit stays lit only as long
       * as its own track does, and picking a different lens releases it.
       */
      const track = stream?.getVideoTracks()[0] ?? null;
      trackRef.current = track;
      if (!stopped) {
        setLamp(false);
        setLampable(hasLamp(track));
      }

      /*
       * Which cameras this phone has, asked for only now.
       *
       * Before permission is granted a browser lists the cameras without
       * naming them — four entries reading "camera", which is not a choice
       * anybody can make. After a stream is open the labels are real.
       */
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (stopped) return;
        setCameras(
          devices
            .filter((device) => device.kind === 'videoinput')
            .map((device) => ({ id: device.deviceId, label: device.label })),
        );
        // Which of them the browser actually opened. Kept apart from the choice
        // a person made, because writing it back into that choice would count
        // as choosing — and this effect restarts the camera when the choice
        // changes, which would then be a stream that reopens itself forever.
        setActive(stream?.getVideoTracks()[0]?.getSettings().deviceId ?? null);
      } catch {
        // A browser that will not enumerate its cameras is a browser with one.
      }
    };

    worker = documentMode ? new DetectWorker() : null;
    if (worker) worker.onmessage = (event: MessageEvent<DetectResult>) => {
      waiting = false;
      if (stopped) return;
      const video = videoRef.current;
      const found = event.data.quad;
      if (!video || !video.videoWidth) return;

      if (!found) {
        // The last outline is left on screen rather than blinking away: a
        // detector that reports nothing on one frame in five would otherwise
        // make the overlay flicker, which reads as the camera being broken.
        steadySince = 0;
        setSteady(false);
        return;
      }

      const shown = scaleQuad(
        found,
        video.videoWidth / (frameRef.current?.width || DETECT_WIDTH),
        video.videoHeight / (frameRef.current?.height || DETECT_WIDTH),
      );
      quadRef.current = shown;
      setQuad(shown);

      const now = performance.now();
      if (!previous || now - previousAt >= DRIFT_SPAN_MS) {
        const moved = previous ? quadDrift(shown, previous, video.videoHeight) : 1;
        previous = shown;
        previousAt = now;
        if (moved > STEADY_DRIFT) steadySince = 0;
        else if (steadySince === 0) steadySince = now;
      }

      const held = steadySince > 0 && now - steadySince >= STEADY_MS;
      setSteady(held);
      // Section 3 — a page held still and out of focus is still out of focus:
      // the shutter waits for the next check to come back sharp.
      const sharp = !checkBlurRef.current || blurry !== true;
      if (held && sharp && autoRef.current && !disabledRef.current && now >= quietUntil) {
        quietUntil = now + COOLDOWN_MS;
        steadySince = 0;
        void shoot();
      }
    };

    /**
     * The frame the detector reads: seventy thousand pixels, off a stream of
     * four million.
     *
     * `createImageBitmap` is asked to do the shrinking because it is the one
     * route that does it where the frame already is. Drawing the video into a
     * canvas that can be read back instead pulls all four million pixels onto
     * the processor and scales them there, on the thread that also draws the
     * interface — which is a stutter in the viewfinder for every look taken.
     */
    const shrink = async (video: HTMLVideoElement, width: number, height: number): Promise<ImageData | null> => {
      let small: ImageBitmap | HTMLVideoElement = video;
      if (!resizeUnsupported) {
        try {
          small = await createImageBitmap(video, {
            resizeWidth: width,
            resizeHeight: height,
            resizeQuality: 'low',
          });
        } catch {
          // Older Safari has the function and not the options; it then scales
          // in the drawing step below, the way this always used to.
          resizeUnsupported = true;
        }
      }

      if (!frameRef.current) frameRef.current = document.createElement('canvas');
      const canvas = frameRef.current;
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return null;
      context.drawImage(small, 0, 0, width, height);
      if (small instanceof ImageBitmap) small.close();
      return context.getImageData(0, 0, width, height);
    };

    /*
     * Section 3 — the blur check, beside the outline's rather than inside it: its
     * own pace, and the page the detector last found as the part to measure.
     */
    const check = (video: HTMLVideoElement, now: number) => {
      if (!checkBlurRef.current) {
        if (blurryRun > 0 || blurry !== null) {
          blurry = null;
          blurryRun = 0;
          setShaky(false);
        }
        return;
      }
      if (checking || now - lastCheck < BLUR_EVERY_MS) return;
      checking = true;
      lastCheck = now;
      void blurScore(video, regionOf(quadRef.current, video.videoWidth, video.videoHeight)).then((score) => {
        checking = false;
        if (stopped) return;
        blurry = score === null ? null : isBlurry(score);
        blurryRun = blurry ? blurryRun + 1 : 0;
        setShaky(blurryRun >= BLUR_LOOKS);
      });
    };

    const look = (now: number) => {
      if (stopped) return;
      raf = requestAnimationFrame(look);

      const video = videoRef.current;
      if (!video || video.readyState < 2 || !video.videoWidth) return;
      check(video, now);
      if (!documentMode || waiting || now - lastLook < LOOK_EVERY_MS) return;
      lastLook = now;

      const width = DETECT_WIDTH;
      const height = Math.max(1, Math.round((video.videoHeight * width) / video.videoWidth));

      // Held from here rather than from the answer: the shrinking is a promise
      // now, and without this the next frame starts one before this one ends.
      waiting = true;
      void shrink(video, width, height)
        .then((pixels) => {
          if (stopped || !pixels || !worker) {
            waiting = false;
            return;
          }
          sequence += 1;
          const request: DetectRequest = { id: sequence, width, height, rgba: pixels.data.buffer };
          worker.postMessage(request, [pixels.data.buffer]);
        })
        .catch(() => {
          waiting = false;
        });
    };

    void start();
    raf = requestAnimationFrame(look);

    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      worker?.terminate();
      releaseCamera(stream, videoRef.current);
      trackRef.current = null;
    };
  }, [shoot, using, documentMode, suspended]);

  const outline = documentMode && picture && natural && quad ? quad : null;

  return (
    <div className="space-y-3">
      <div ref={setBox} className="relative w-full h-[46vh] min-h-[220px] rounded-xl overflow-hidden bg-foreground">
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-contain"
          playsInline
          muted
          onLoadedMetadata={(event) => {
            const video = event.currentTarget;
            setNatural({ width: video.videoWidth, height: video.videoHeight });
          }}
        />

        {outline && picture && natural && (
          <svg
            className="absolute pointer-events-none"
            style={{ left: picture.left, top: picture.top, width: picture.width, height: picture.height }}
            viewBox={`0 0 ${natural.width} ${natural.height}`}
            preserveAspectRatio="none"
            aria-hidden
          >
            {/*
              Everything outside the page, dimmed. The hole is the second
              sub-path of one filled shape rather than a second element, which
              is what `evenodd` is for — and is why the dimming never leaves a
              seam along the page's edge the way two stacked shapes do.
            */}
            <path
              d={`M0 0H${natural.width}V${natural.height}H0Z M${outline.map((p: Point) => `${p.x} ${p.y}`).join('L')}Z`}
              fillRule="evenodd"
              className="fill-foreground/45"
            />
            <polygon
              points={outline.map((p: Point) => `${p.x},${p.y}`).join(' ')}
              className={steady ? 'fill-none stroke-primary' : 'fill-none stroke-background/90'}
              strokeWidth={Math.max(2, natural.width / 180)}
              strokeLinejoin="round"
            />
          </svg>
        )}

        {status !== 'live' && (
          <p className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-background/90">
            {t(`scan.camera.${status}`)}
          </p>
        )}

        {/* Section 3 — blurry for about a second says so, in the line that
            otherwise says what the camera is doing, on a chip that reads over
            any picture. */}
        {status === 'live' && checkBlur && shaky ? (
          <p role="status" className="absolute inset-x-0 top-2 flex justify-center px-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-background/95 px-3 py-1.5 text-xs font-semibold text-warning shadow">
              <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {t('invoiceScan.blur.live')}
            </span>
          </p>
        ) : (
          status === 'live' && documentMode && (
            <p className="absolute inset-x-0 top-2 text-center text-xs text-background/90 drop-shadow">
              {t(quad ? (steady ? 'scan.camera.holding' : 'scan.camera.found') : 'scan.camera.searching')}
            </p>
          )
        )}

        {/*
          The lens picker, on the picture rather than under it.

          A phone with three rear cameras opens whichever one its browser calls
          the environment-facing camera, and on most of them that is the
          ultra-wide — which photographs a document from a foot away with the
          page occupying a third of the frame and the corners bowed. The
          picture is where the difference is visible, so the control is there.
        */}
        {status === 'live' && (cameras.length > 1 || lampable) && (
          <div className="absolute bottom-2 start-2 end-2 flex items-end gap-2">
            {cameras.length > 1 && (
              <div className="flex min-w-0 flex-col items-start gap-1">
                {picking &&
                  cameras.map((camera, index) => (
                    <button
                      key={camera.id}
                      type="button"
                      onClick={() => {
                        setUsing(camera.id);
                        setPicking(false);
                        setQuad(null);
                        setNatural(null);
                      }}
                      aria-pressed={camera.id === (using ?? active)}
                      className={`max-w-full truncate px-3 py-2 rounded-lg text-xs backdrop-blur min-h-11 ${
                        camera.id === (using ?? active)
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background/85 text-foreground hover:bg-background'
                      }`}
                    >
                      {camera.label || t('scan.camera.numbered', { index: index + 1 })}
                    </button>
                  ))}
                <button
                  type="button"
                  onClick={() => setPicking((open) => !open)}
                  aria-expanded={picking}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs bg-background/85 backdrop-blur hover:bg-background min-h-11"
                >
                  <SwitchCamera className="h-4 w-4" aria-hidden />
                  {t('scan.camera.lens')}
                </button>
              </div>
            )}

            {/*
              The lamp, at the far end of the same row.

              Nothing about a document says it needs one, and then somebody
              signs a delivery note in a stairwell at six in the evening. It is
              a two-state switch rather than an automatic thing, because a lamp
              that decides for itself when a page is dark enough is a lamp that
              fires in a customer's face.
            */}
            {lampable && (
              <button
                type="button"
                onClick={() => void flip()}
                aria-pressed={lamp}
                className={`ms-auto flex shrink-0 items-center gap-1.5 px-3 py-2 rounded-lg text-xs backdrop-blur min-h-11 ${
                  lamp
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background/85 text-foreground hover:bg-background'
                }`}
              >
                {lamp ? (
                  <Flashlight className="h-4 w-4" aria-hidden />
                ) : (
                  <FlashlightOff className="h-4 w-4" aria-hidden />
                )}
                {t('scan.camera.lamp')}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onPickFiles}
          className="flex items-center gap-1.5 p-2 rounded-lg text-sm text-muted-foreground hover:bg-muted min-h-11"
        >
          <Images className="h-5 w-5" aria-hidden />
          {t('scan.addFile')}
        </button>

        {/* The shutter stays even with automatic capture on. Automatic is a
            guess about when the picture is right, and the person holding the
            phone is allowed to disagree with it. */}
        <button
          type="button"
          onClick={() => void shoot()}
          disabled={disabled || status !== 'live'}
          aria-label={t('scan.camera.shutter')}
          className="h-16 w-16 rounded-full border-4 border-primary/40 bg-primary disabled:opacity-40 active:scale-95 transition-transform shrink-0"
        />

        {documentMode && <button
          type="button"
          onClick={() => setAutoShutter((on) => !on)}
          aria-pressed={autoShutter}
          className={`flex items-center gap-1.5 p-2 rounded-lg text-sm min-h-11 ${
            autoShutter ? 'text-primary' : 'text-muted-foreground'
          } hover:bg-muted`}
        >
          {autoShutter ? <Zap className="h-5 w-5" aria-hidden /> : <ZapOff className="h-5 w-5" aria-hidden />}
          {t('scan.camera.auto')}
        </button>}
      </div>
    </div>
  );
}
